// The refill sheet's modal shell: the app's own modal (base.css .modal-backdrop /
// .modal / .modal-head / .modal-body / .modal-foot — the classes confirm-dialog.jsx
// uses), with the same minimal focus trap. Focus moves INTO the dialog when it opens
// (the `initialFocus` ref, else the first control), Tab cycles inside it, Escape and a
// backdrop click close it, and focus goes back to whatever opened it.
import React, { useEffect, useRef } from 'react';

const FOCUSABLE = 'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

export function SheetModal({ title, subtitle, onClose, children, footer, initialFocus, className = '', labelId }) {
  const dialogRef = useRef(null);
  const restoreRef = useRef(null);
  const headingId = labelId || 'rs-modal-title';

  useEffect(() => {
    restoreRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    const target = (initialFocus && initialFocus.current)
      || (dialogRef.current && dialogRef.current.querySelector(FOCUSABLE));
    if (target && typeof target.focus === 'function') target.focus();
    return () => {
      const el = restoreRef.current;
      if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); if (onClose) onClose(); return; }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return (
    <div className="modal-backdrop rs-modal-backdrop" onClick={() => onClose && onClose()}>
      <div
        ref={dialogRef}
        className={`modal rs-modal ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="modal-head rs-modal-head">
          <div>
            <h3 id={headingId}>{title}</h3>
            {subtitle && <div className="sub">{subtitle}</div>}
          </div>
          <button type="button" className="rs-modal-x" aria-label="Close" onClick={() => onClose && onClose()}>×</button>
        </div>
        <div className="modal-body rs-modal-body">{children}</div>
        {footer && <div className="modal-foot rs-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
